import { GrapplerLogger, FETCH_MECHANISM } from 'saddlebag-grappler';
import { logWarn, logError } from 'saddlebag-logger';

import Message, { isValidGuid } from './message';

/**
 * @typedef {Object} Message
 * @property {uuid} guid The header guid
 */

/**
 * Base event logger to be extended to send events.
 *
 * @constructor
 * @param {string} environment The environment to log to. Can be either public or public-sandbox
 * @param {string} component The name of the service that is logging the events
 * @param {object} options an object containing the following options
 * @param {boolean} options.enhance enhance the message with a header
 * @param {string} options.mechanism the mechanism to use to send the event
 * @param {string} options.page_name the page_name of the owner of the eventtracker
 * @param {string} options.service_name the service_name of the owner of the eventtracker
 * @param {boolean} options.useAbsoluteUrl when true, use absolute URL (https://slipstream.skyscanner.net/slipstream) instead of relative URL (/slipstream)
 * @param {object} options.batching the object containing batching options
 * @param {boolean} options.batching.enabled boolean to decide whether to batch messages when possible
 * @param {number} options.batching.maxBufferSize the maximum size of the buffer before sending
 * @param {number} options.batching.flushAfter the time in milliseconds to wait before sending the buffer
 */
export default class BaseEventsTracking {
  constructor(
    environment,
    component,
    options = {
      enhance: false,
      mechanism: FETCH_MECHANISM,
      useAbsoluteUrl: false,
    },
  ) {
    this.grapplerLogger = new GrapplerLogger(environment, component, options);
    this.page_name = options.page_name || undefined;
    this.service_name = options.service_name || undefined;
    this.sequenceNum = 0;
  }

  /**
   * Log a mini/funnel event to Grappler. The message body will be enriched with a mini header.
   *
   * @param {string} eventName Event Name of the event e.g. funnel_event or flight_search
   * @param {string} fullSchemaName The full schema name of the event e.g. clients.Search
   * @param {Object} message The message body of the event. This needs to match the schema
   * defined in the schemas repository extactly or it will be rejected by Slipstream. Fields can
   * be omitted
   * @param {Object} opts Overriding options for logging
   * @param {uuid} opts.messageGuid A guid to override the message's header guid
   * @param {boolean} [opts.priority=false] Flag to specify if message should be sent instantly when using batching
   * @returns {Promise<Message>} A promise to the message header guid
   */
  async trackEvent(eventName, fullSchemaName, message, opts = {}) {
    if (typeof message !== 'object') {
      throw new Error('message should be an Object');
    }
    if (!isValidGuid(opts)) {
      throw new Error(
        'The specified guid is invalid. It must conform to ^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$',
      );
    }

    try {
      const event = new Message(fullSchemaName, {
        page_name: this.page_name,
        service_name: this.service_name,
        ...opts,
        sequence_number: (this.sequenceNum += 1),
      });
      await event.buildMessage(message);

      const promise = this.grapplerLogger
        .sendEvent(eventName, fullSchemaName, event.get(), opts)
        .catch((reason) => {
          logWarn({
            component: 'saddlebag-user-tracking-events',
            message: String(reason),
            event: fullSchemaName,
          });
        });

      return { guid: event.get().header.guid, promise };
    } catch (e) {
      logError(e, { event: fullSchemaName });
    }

    return {};
  }

  /**
   * Log a mini/funnel event to Grappler. The message body will be enriched with a mini header.
   *
   * @param {string} eventName Event Name of the event e.g. funnel_event or flight_search
   * @param {string} fullSchemaName The full schema name of the event e.g. clients.Search
   * @param {Object[]} messages A list of message bodies of the event. Each event needs to match
   * the schema defined in the schemas repository exactly or it will be rejected by Slipstream.
   * Fields can be omitted
   * @param {Object} opts Overriding options for logging
   * @param {uuid} opts.messageGuid A guid to override the message's header guid
   * @returns {Promise<Message>[]} A array of promises to the message header guids
   */
  async trackBatchEvent(eventName, fullSchemaName, messages, opts = {}) {
    if (!isValidGuid(opts)) {
      throw new Error(
        'The specified guid is invalid. It must conform to ^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$',
      );
    }

    try {
      const messagePromises = messages.map((message) => {
        if (typeof message !== 'object') {
          throw new Error('message should be an Object');
        }
        const event = new Message(fullSchemaName, {
          ...opts,
          sequence_number: (this.sequenceNum += 1),
        });
        return event.buildMessage(message);
      });

      const finalisedMessages = await Promise.all(messagePromises);
      this.grapplerLogger
        .sendEvents(eventName, fullSchemaName, finalisedMessages)
        .catch((reason) =>
          logWarn({
            component: 'saddlebag-user-tracking-events',
            message: String(reason),
            event: fullSchemaName,
          }),
        );

      return finalisedMessages.map((message) => ({
        guid: message.header.guid,
      }));
    } catch (e) {
      logError(e, { event: fullSchemaName });
    }

    return {};
  }
}
